/**
 * REST client for /wp-json/slotshurra/v1/plinko/.
 *
 * - Logged-in: cookie auth + X-WP-Nonce from PlinkoBoot.
 * - Demo: X-Plinko-Demo session id, kept in sessionStorage for the tab.
 * - drop(): one X-Round-Key per attempt; a network failure or 5xx is retried
 *   ONCE with the same key, so a timeout on a winning round can never
 *   double-charge or double-pay. 4xx is never retried.
 * - Money fields arrive as strings; they are converted to numbers exactly once,
 *   here, and never re-derived by the UI.
 */
export class PlinkoApi {
  constructor(boot) {
    this.base = boot.rest;
    this.nonce = boot.nonce;
    this.demo = !!boot.demo;
    this.demoId = this.demo ? demoSession() : null;
  }

  headers(extra = {}) {
    const h = { 'Content-Type': 'application/json', ...extra };
    if (this.demo) h['X-Plinko-Demo'] = this.demoId;
    else h['X-WP-Nonce'] = this.nonce;
    return h;
  }

  async request(method, path, body, extra, retryOnce = false) {
    const opts = { method, headers: this.headers(extra), credentials: 'same-origin' };
    if (body !== undefined) opts.body = JSON.stringify(body);
    let attempt = 0;
    for (;;) {
      attempt++;
      let res;
      try {
        res = await fetch(this.base + path, opts);
      } catch (e) {
        if (retryOnce && attempt === 1) continue;           // network: same key, once
        throw new ApiError('network', 'Connection lost. Check your connection and try again.', 0);
      }
      let data = null;
      try { data = await res.json(); } catch { /* non-JSON body */ }
      if (res.ok) return data;
      if (res.status >= 500 && retryOnce && attempt === 1) continue;
      const code = data && data.code ? data.code : `http_${res.status}`;
      const msg = data && data.message ? data.message : `Server error (${res.status}).`;
      throw new ApiError(code, msg, res.status, data && data.data ? data.data : {});
    }
  }

  async config() {
    const c = await this.request('GET', 'config');
    c.balance = Number(c.balance);
    return c;
  }

  /** @returns {Promise<Round>} */
  async drop(balls, rows, risk) {
    const key = uuid4();
    const r = await this.request('POST', 'drop', { balls, rows, risk }, { 'X-Round-Key': key }, true);
    return normaliseRound(r);
  }

  rotate(clientSeed) {
    return this.request('POST', 'rotate-seed', clientSeed ? { client_seed: clientSeed } : {});
  }

  history(limit = 20) { return this.request('GET', `history?limit=${limit}`); }

  async demoReset() {
    const r = await this.request('POST', 'demo-reset');
    return Number(r.balance);
  }
}

export class ApiError extends Error {
  constructor(code, message, status, data = {}) { super(message); this.code = code; this.status = status; this.data = data; }
}

/** Server money strings → numbers, once. Ball shapes match what the presentation expects. */
function normaliseRound(r) {
  const ball = b => ({
    miss: !!b.miss,
    bucket: b.miss ? null : b.bucket,
    slot: b.miss ? null : (b.slot === undefined ? null : b.slot),
    zone: !!b.zone,
    bonusZonePay: !!b.bonus_zone_pay,
    multiplier: Number(b.multiplier),
    win: Number(b.win),
    pathId: b.path_id,
  });
  return {
    roundId: r.round_id,
    balls: r.balls, rows: r.rows, risk: r.risk,
    costPerBall: r.cost_per_ball,
    stake: Number(r.stake),
    results: r.results.map(ball),
    bonuses: r.bonuses.map(fb => fb.map(ball)),
    gutters: r.gutters,
    voided: r.voided,
    collectedBefore: r.collected_before,
    progress: r.progress,
    collectTarget: r.collect_target,
    freeBalls: r.free_balls,
    zonePays: Number(r.zone_pays),
    bonusWin: Number(r.bonus_win),
    win: Number(r.win),
    capped: !!r.capped,
    maxWin: r.max_win === null || r.max_win === undefined ? null : Number(r.max_win),
    balance: Number(r.balance),
    demo: !!r.demo,
    settled: r.settled !== false,
    fair: r.fair,
  };
}

function demoSession() {
  try {
    let id = sessionStorage.getItem('plinko_demo');
    if (!id) { id = 'd' + uuid4().replace(/-/g, ''); sessionStorage.setItem('plinko_demo', id); }
    return id;
  } catch { return 'd' + uuid4().replace(/-/g, ''); }
}

export function uuid4() {
  if (globalThis.crypto && crypto.randomUUID) return crypto.randomUUID();
  const b = new Uint8Array(16); crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
